
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import React from "react";
import useTranslation from "next-translate/useTranslation";

import { Head } from "@/components/Head";
import { DashboardScreenSection } from "@/sections/DashboardScreenSection";
import { CustomersLogosSection } from "@/sections/CustomersLogosSection";
import { useTheme } from "@/hooks/useTheme";
import { PricingSectionFigmaBackup } from "@/sections/PricingSectionFigmaBackup";
import { CALENDLY_LINK } from "@/constants/pricing";
import { FigmaBackupTopScreen } from "@/sections/FigmaBackupTopScreen";
import { HowItWorks } from "@/sections/HowItWorks";
import { BackupFaqSection } from "@/sections/FigmaBackupFaqSection";
import { signUpForFreeTrialAccount } from "@/services/freeTrialAccountService";
import { GetServerSideProps, InferGetServerSidePropsType } from "next";
import { withSession } from "@/utils/session";
import { TryOutSectionBackup } from "@/components/TryOutSectionFigmaBackup";
import { CTASection } from "@/sections/CTASection";
import FigmaBackupPlugin from "@/sections/FigmaBackupPlugin";

const FigmaDesignBackup = ({
  figmaAccessToken,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const { t } = useTranslation();
  const { bp } = useTheme();

  return (
    <>
      <Head
        pageNamespace="page/figma-design-backup"
        ogImage="/images/figma-backup-og-image.jpg"
      />

      <FigmaBackupTopScreen
        title={t("page/figma-design-backup:title")}
        subtitle={t("page/figma-design-backup:subtitle")}
        src="/images/figma-backup-screen.webp"
        imageStyles={{
          borderRadius: 12,
          filter: "drop-shadow(0px 24px 48px rgba(16, 5, 48, 0.14))",
        }}
        onButtonClick={signUpForFreeTrialAccount}
        buttonLabel={t("common:tryForFree")}
        buttonSecondaryLabel={t("common:seePricing")}
      />

      <CustomersLogosSection
        title={t("section/trustedBy:title")}
        customStyle={{
          [bp.FROM_DESKTOP]: { paddingTop: 0 },
          borderBottom: "1px solid #E5EAF4",
        }}
      />

      <DashboardScreenSection
        title={t("page/figma-design-backup:section/storeDesign.title")}
        src="/images/figma-backup-versioning.png"
        imageStyles={{ border: "1px solid #E5EAF4" }}
        elements={[
          {
            id: "ELEMENT#1",
            iconType: "CMS-ICON",
            text: t(
              "page/figma-design-backup:section/storeDesign.elements.avoidLockIn",
            ),
          },
          {
            id: "ELEMENT#2",
            iconType: "CMS-ICON",
            text: t(
              "page/figma-design-backup:section/storeDesign.elements.figjam",
            ),
          },
          {
            id: "ELEMENT#3",
            iconType: "CMS-ICON",
            text: t(
              "page/figma-design-backup:section/storeDesign.elements.automatedConversion",
            ),
          },
          {
            id: "ELEMENT#4",
            iconType: "CMS-ICON",
            text: t(
              "page/figma-design-backup:section/storeDesign.elements.securedStorage",
            ),
          },
        ]}
        onButtonClick={signUpForFreeTrialAccount}
        tryFreeLabel={t("common:tryForFree")}
        scheduleDemo={CALENDLY_LINK}
        scheduleDemoLabel={t("common:scheduleDemo")}
      />
      <DashboardScreenSection
        title={t("page/figma-design-backup:section/backupSection.title")}
        src="/images/figma-backup-storage.png"
        imageStyles={{ border: "1px solid #E5EAF4" }}
        elements={[
          {
            id: "ELEMENT#1",
            iconType: "CMS-ICON",
            text: t(
              "page/figma-design-backup:section/backupSection.elements.avoidLockIn",
            ),
          },
          {
            id: "ELEMENT#2",
            iconType: "CMS-ICON",
            text: t(
              "page/figma-design-backup:section/backupSection.elements.automatedConversion",
            ),
          },
          {
            id: "ELEMENT#3",
            iconType: "CMS-ICON",
            text: t(
              "page/figma-design-backup:section/backupSection.elements.securedStorage",
            ),
          },
        ]}
        onButtonClick={signUpForFreeTrialAccount}
        tryFreeLabel={t("common:tryForFree")}
        scheduleDemo={CALENDLY_LINK}
        scheduleDemoLabel={t("common:scheduleDemo")}
        wrapperStyles={{
          backgroundImage: "url('images/square-bg-big.png')",
          [bp.FROM_TABLET]: {
            padding: "40px 20px 120px",
          },
          nested: {
            "& > div": {
              borderTop: "none",
            },
          },
        }}
        rightSidedImage={false}
      />

      <FigmaBackupPlugin />

      <HowItWorks
        title={t("page/figma-design-backup:section/howItWorks.title")}
        steps={[
          {
            id: "STEP#1",
            image: "/images/backup-authorize.png",
            text: t(
              "page/figma-design-backup:section/howItWorks.steps.authorize",
            ),
            rightSidedImage: true,
          },
          {
            id: "STEP#2",
            image: "/images/backup-add-file.png",
            text: t(
              "page/figma-design-backup:section/howItWorks.steps.insertFiles",
            ),
          },
          {
            id: "STEP#3",
            image: "/images/backup-success.png",
            text: t("page/figma-design-backup:section/howItWorks.steps.backup"),
            rightSidedImage: true,
          },
        ]}
        wrapperStyles={{
          backgroundImage: "url('images/square-bg-big.png')",
          nested: {
            "> div": {
              [bp.FROM_TABLET]: {
                padding: "100px 20px 80px",
              },
            },
          },
        }}
      />

      <PricingSectionFigmaBackup figmaAccessToken={figmaAccessToken} />

      <TryOutSectionBackup
        title={t("page/figma-design-backup:section/tryOut.title")}
        subtitle={t("page/figma-design-backup:section/tryOut.subtitle")}
        src="/images/figma-backup-vector.svg"
        elements={[
          {
            id: "ELEMENT#1",
            iconType: "CMS-ICON",
            text: t(
              "page/figma-design-backup:section/tryOut.elements.automated",
            ),
          },
          {
            id: "ELEMENT#2",
            iconType: "CMS-ICON",
            text: t(
              "page/figma-design-backup:section/tryOut.elements.noCreditCard",
            ),
          },
          {
            id: "ELEMENT#3",
            iconType: "CMS-ICON",
            text: t("page/figma-design-backup:section/tryOut.elements.signUp"),
          },
        ]}
      />

      <BackupFaqSection />

      <CTASection
        title={t("section/cta:titles.figmaDesignBackupPage")}
        buttonLabel={t(
          "page/figma-design-backup:section/storeDesign.buttonLabel",
        )}
        href={CALENDLY_LINK}
        wrapperBgColor="#F8F9FE"
      />
    </>
  );
};

export default FigmaDesignBackup;

 const _getServerSideProps: GetServerSideProps = withSession(
  async context => {
    const code = context.req.session.get("figmaAccessToken");

    return {
      props: {
        figmaAccessToken: code || null,
      },
    };
  },
);


// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/figma-design-backup',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  